// Native blog articles served at /blog/<slug>.
//
// This registry is the single source of truth for the blog index on /blog, the
// per-article metadata (title, description, dates, keywords), the author block,
// the BlogPosting schema and the related-article links. The article body lives
// next to its route component in src/pages/blog/<Component>.jsx.
//
// Adding an article: add an entry here, create the page component, register the
// route in App.jsx (above the /blog/:slug redirect) and add the meta pair to
// src/lib/seo/pageContent.js (and, for completeness, base44/functions/seoManifest).
// The sitemap, llms.txt and rss.xml are regenerated from this registry before every
// build (scripts/generate-seo-files.mjs, package.json "prebuild"); the build fails if
// a /blog/<slug> route in App.jsx has no entry here, so a new article cannot ship
// unlisted.

export const BLOG_PATH = '/blog';
const SITE = 'https://trinitydentalarts.com';

export const BLOG_AUTHOR = {
  id: `${SITE}/#dr-gjelaj`,
  name: 'Dr. Xhoana Gjelaj',
  credentials: 'DMD, MAGD, DABOI/ID, DICOI, FAAID',
  jobTitle: 'Owner and lead dentist, Trinity Dental Arts',
  url: '/meet-the-doctor',
  image: 'https://media.base44.com/images/public/697e88817b05b4051a928e3f/28f39a315_138836.jpg',
  sameAs: ['https://aaid-implant.org/dentist/gjelaj-xhoana/'],
  bio:
    'Dr. Xhoana Gjelaj is the owner and lead dentist of Trinity Dental Arts in Trinity, Florida, where she has focused on implant dentistry for more than 20 years. ' +
    'She holds Mastership in the Academy of General Dentistry (MAGD), board certification in implant dentistry as a Diplomate of the American Board of Oral Implantology/Implant Dentistry (DABOI/ID), ' +
    'Diplomate status with the International Congress of Oral Implantologists (DICOI) and Fellowship in the American Academy of Implant Dentistry (FAAID). ' +
    'Her 2,000+ hours of post-graduate training include the Misch International Implant Institute, The Pankey Institute, The Dawson Academy and Spear Education.',
};

export const BLOG_ARTICLES = [
  {
    slug: 'what-are-dental-implants',
    component: 'WhatAreDentalImplants',
    category: 'Dental Implants',
    headline: 'What Are Dental Implants?',
    headlineAccent: 'How They Work, Who Qualifies, and What to Expect',
    metaTitle: "What Are Dental Implants? A Dentist's Guide | Trinity, FL",
    metaDescription:
      'How dental implants work, who qualifies, what each stage involves and what they cost in Trinity, FL, explained by implantologist Dr. Xhoana Gjelaj.',
    excerpt:
      'The implant, the abutment and the crown, the healing timeline, who qualifies, and the published costs — explained in plain language.',
    keywords: ['dental implants', 'what are dental implants', 'how do dental implants work', 'dental implant procedure', 'dental implants Trinity FL'],
    datePublished: '2026-11-01',
    dateModified: '2026-11-04',
    readingMinutes: 8,
    socialImage: 'implants',
    relatedPages: [
      { label: 'Dental implants at Trinity Dental Arts', href: '/dental-implants' },
      { label: 'Single tooth implants', href: '/single-implant' },
      { label: 'What implants cost, itemized', href: '/dental-implant-cost' },
      { label: 'Do implants hurt?', href: '/do-dental-implants-hurt' },
      { label: 'Bone grafting', href: '/bone-grafting' },
      { label: 'Dental implants FAQ', href: '/dental-implants-faq' },
      { label: 'Dental implant treatment guide', href: '/dental-implants-guide' },
      { label: 'Dental bridges', href: '/dental-bridges' },
    ],
  },
  {
    slug: 'dental-implants-near-me-trinity-fl',
    component: 'DentalImplantsNearMeTrinityFL',
    category: 'Choosing a Provider',
    headline: 'How to Find a Dental Implant Dentist Near Trinity, FL',
    headlineAccent: 'Seven questions to ask before booking a consultation',
    metaTitle: 'Dental Implants Near Me: What to Check First | Trinity, FL',
    metaDescription:
      'Searching for dental implants near Trinity or New Port Richey? Seven things to verify before you book: credentials, 3D imaging, pricing and who operates.',
    excerpt:
      'Seven things to check before booking an implant consultation near Trinity or New Port Richey.',
    keywords: ['dental implants near me', 'dental implants Trinity FL', 'dental implants New Port Richey', 'implant dentist near me', 'dental implants Pasco County'],
    datePublished: '2026-11-01',
    dateModified: '2026-11-04',
    readingMinutes: 7,
    socialImage: 'local-planning',
    relatedPages: [
      { label: 'Dental implants in Trinity, FL', href: '/dental-implants-trinity-fl' },
      { label: 'Dental implants near New Port Richey', href: '/dental-implants-new-port-richey-fl' },
      { label: 'How to choose a dentist in Trinity', href: '/how-to-choose-a-dentist-in-trinity-fl' },
      { label: 'Patient reviews', href: '/patient-reviews' },
      { label: 'Before and after results', href: '/our-results' },
      { label: 'Directions and contact', href: '/contact-us' },
    ],
  },
  {
    slug: 'what-is-an-implant-dentist',
    component: 'WhatIsAnImplantDentist',
    category: 'Choosing a Provider',
    headline: 'What Is an Implant Dentist?',
    headlineAccent: 'Training, Credentials and the Questions Worth Asking',
    metaTitle: 'Implant Dentist Credentials Explained | Trinity Dental Arts',
    metaDescription:
      'Any licensed dentist can place implants. Dr. Gjelaj explains what an implant dentist is, what board certification means and what to ask first.',
    excerpt:
      'Implant dentistry is not a recognized dental specialty, so the title is unregulated. Here is how to read the credentials that actually mean something.',
    keywords: ['implant dentist', 'implant dentist near me', 'board certified implant dentist', 'implantologist', 'DABOI/ID'],
    datePublished: '2026-11-01',
    dateModified: '2026-11-04',
    readingMinutes: 6,
    socialImage: 'education',
    relatedPages: [
      { label: 'Meet Dr. Gjelaj', href: '/meet-the-doctor' },
      { label: 'Implant dentist in Trinity, FL', href: '/best-implant-dentist-trinity-fl' },
      { label: 'How to choose a dentist', href: '/how-to-choose-a-dentist-in-trinity-fl' },
      { label: 'Our technology', href: '/our-technology' },
      { label: 'In-house dental lab', href: '/in-house-lab' },
      { label: 'Sedation dentistry', href: '/sedation-dentistry' },
    ],
  },
  {
    slug: 'all-on-4-dental-implants-explained',
    component: 'AllOn4DentalImplantsExplained',
    category: 'Full-Arch Implants',
    headline: 'All-on-4 Dental Implants, Explained',
    headlineAccent: 'How Four Implants Support a Full Arch of Fixed Teeth',
    metaTitle: 'All-on-4 Dental Implants Explained | Trinity Dental Arts',
    metaDescription:
      'How All-on-4 dental implants work: why the back implants are tilted, who qualifies, surgery day, healing, long-term research and published pricing.',
    excerpt:
      'Why four implants can carry a full arch, what the tilted back implants accomplish, who qualifies, and what the research says about long-term results.',
    keywords: ['all on 4 dental implants', 'All-on-4', 'all on four implants', 'full arch dental implants', 'All-on-X'],
    datePublished: '2026-11-01',
    dateModified: '2026-11-04',
    readingMinutes: 8,
    socialImage: 'implants',
    relatedPages: [
      { label: 'All-on-4 and All-on-X', href: '/all-on-x' },
      { label: 'FiXed New Teeth full-arch program', href: '/fixed-new-teeth' },
      { label: 'All-on-4 in Trinity, FL', href: '/all-on-4-trinity-fl' },
      { label: 'All-on-4 vs. All-on-6', href: '/all-on-4-vs-all-on-6' },
      { label: 'All-on-4 vs. dentures', href: '/all-on-4-vs-dentures' },
      { label: 'All-on-4 treatment guide', href: '/all-on-4-guide' },
      { label: 'Published full-arch pricing', href: '/fixed-new-teeth#pricing' },
    ],
  },
  {
    slug: 'full-mouth-dental-implants-options',
    component: 'FullMouthDentalImplantsOptions',
    category: 'Full-Arch Implants',
    headline: 'Full-Mouth Dental Implant Options',
    headlineAccent: 'Compare fixed bridges, removable overdentures and individual restorations',
    metaTitle: 'Full Mouth Dental Implants: Options Compared | Trinity, FL',
    metaDescription:
      'Full mouth dental implants compared: fixed full-arch teeth, snap-in overdentures and implant bridges. Candidacy, stages, maintenance and published costs.',
    excerpt:
      'Fixed full-arch teeth, implant-retained overdentures or segmented implant bridges: how the three approaches differ in surgery, daily life, maintenance and cost.',
    keywords: ['full mouth dental implants', 'full mouth implants cost', 'replace all teeth with implants', 'full arch implants', 'full mouth reconstruction implants'],
    datePublished: '2026-11-01',
    dateModified: '2026-11-04',
    readingMinutes: 7,
    socialImage: 'comparison',
    relatedPages: [
      { label: 'Full mouth reconstruction', href: '/full-mouth-restoration' },
      { label: 'FiXed New Teeth full-arch program', href: '/fixed-new-teeth' },
      { label: 'Fixed vs. removable implant teeth', href: '/fixed-vs-removable-implant-teeth' },
      { label: 'What everything costs', href: '/dental-implant-cost' },
      { label: 'Sinus grafts', href: '/sinus-grafts' },
      { label: 'Sedation for long visits', href: '/sedation-dentistry' },
    ],
  },
  {
    slug: 'teeth-in-a-day-how-it-works',
    component: 'TeethInADayHowItWorks',
    category: 'Same-Day Implants',
    headline: 'How Do Teeth in a Day Work?',
    headlineAccent: 'What to expect from surgery day through your final bridge',
    metaTitle: 'Teeth in a Day: How It Really Works | Trinity Dental Arts',
    metaDescription:
      'Teeth in a day explained: what happens on surgery day, why the same-day teeth are provisional, how implant stability decides eligibility, and what follows.',
    excerpt:
      'The phrase is accurate and incomplete at the same time. What you leave with on surgery day, what happens over the next months, and who it is not for.',
    keywords: ['teeth in a day', 'teeth in one day', 'same day teeth', 'immediate load implants', 'fixed provisional teeth'],
    datePublished: '2026-11-01',
    dateModified: '2026-11-04',
    readingMinutes: 6,
    socialImage: 'implants',
    relatedPages: [
      { label: 'Same-day dental implants in Trinity, FL', href: '/same-day-dental-implants-trinity-fl' },
      { label: 'FiXed New Teeth full-arch program', href: '/fixed-new-teeth' },
      { label: 'All-on-4 and All-on-X', href: '/all-on-x' },
      { label: 'In-house dental lab', href: '/in-house-lab' },
      { label: 'Our technology', href: '/our-technology' },
      { label: 'Sedation dentistry', href: '/sedation-dentistry' },
    ],
  },
  {
    slug: 'same-day-dental-implants-candidates',
    component: 'SameDayDentalImplantsCandidates',
    category: 'Same-Day Implants',
    headline: 'Who Is a Candidate for Same-Day Dental Implants?',
    headlineAccent: 'How your dentist decides when to place implants and attach temporary teeth',
    metaTitle: 'Same-Day Dental Implants: Who Qualifies? | Trinity, FL',
    metaDescription:
      'Who qualifies for same-day dental implants? Bone, implant stability, infection, smoking and health factors, plus the staged plan when waiting is safer.',
    excerpt:
      'Same-day placement and same-day teeth are two different decisions. The clinical factors that decide each one, and what the staged alternative looks like.',
    keywords: ['same day dental implants', 'immediate dental implants', 'same day implant candidates', 'immediate implant placement', 'immediate loading'],
    datePublished: '2026-11-01',
    dateModified: '2026-11-04',
    readingMinutes: 6,
    socialImage: 'implants',
    relatedPages: [
      { label: 'Same-day dental implants in Trinity, FL', href: '/same-day-dental-implants-trinity-fl' },
      { label: 'Tooth extractions', href: '/tooth-extractions' },
      { label: 'Bone grafting', href: '/bone-grafting' },
      { label: 'Single tooth implants', href: '/single-implant' },
      { label: 'Gum disease treatment', href: '/periodontal-disease-treatment' },
      { label: 'Too old for implants?', href: '/too-old-for-dental-implants' },
    ],
  },
  {
    slug: 'implant-dentures-snap-in-vs-fixed',
    component: 'ImplantDenturesSnapInVsFixed',
    category: 'Tooth Replacement Options',
    headline: 'Snap-In Dentures vs. Fixed Implant Teeth',
    headlineAccent: 'How each option feels, stays in place and needs to be cleaned',
    metaTitle: 'Implant Dentures: Snap-In vs. Fixed Teeth | Trinity, FL',
    metaDescription:
      'Implant dentures explained: how snap-in overdentures work, how they differ from fixed implant teeth, what each costs to maintain, and why we favor fixed.',
    excerpt:
      'How a snap-in overdenture works, where it falls short, what fixed implant teeth change, and the maintenance each one asks of you over the years.',
    keywords: ['implant dentures', 'implant supported dentures', 'snap in dentures', 'overdenture', 'implant retained dentures'],
    datePublished: '2026-11-01',
    dateModified: '2026-11-04',
    readingMinutes: 6,
    socialImage: 'comparison',
    relatedPages: [
      { label: 'Snap-in dentures vs. fixed teeth', href: '/snap-in-dentures' },
      { label: 'Fixed vs. removable implant teeth', href: '/fixed-vs-removable-implant-teeth' },
      { label: 'Dentures', href: '/dentures' },
      { label: 'Implants vs. dentures', href: '/dental-implants-vs-dentures' },
      { label: 'Loose dentures?', href: '/loose-dentures-trinity-fl' },
      { label: 'FiXed New Teeth full-arch program', href: '/fixed-new-teeth' },
    ],
  },
];

export const blogArticlePath = (article) => `${BLOG_PATH}/${article.slug}`;
export const blogArticleUrl = (article) => `${SITE}${blogArticlePath(article)}`;

export function getBlogArticle(slug) {
  const article = BLOG_ARTICLES.find((a) => a.slug === slug);
  if (!article) throw new Error(`Unknown blog article: ${slug}`);
  return article;
}

/** Other articles, same category first, newest first. */
export function relatedBlogArticles(slug, limit = 3) {
  const current = getBlogArticle(slug);
  return BLOG_ARTICLES
    .filter((a) => a.slug !== slug)
    .sort((a, b) => {
      const sameA = a.category === current.category ? 0 : 1;
      const sameB = b.category === current.category ? 0 : 1;
      if (sameA !== sameB) return sameA - sameB;
      return b.datePublished.localeCompare(a.datePublished);
    })
    .slice(0, limit);
}

/** Articles for the /blog index, newest first. */
export function publishedBlogArticles() {
  return [...BLOG_ARTICLES].sort((a, b) => b.datePublished.localeCompare(a.datePublished));
}

export function formatBlogDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
}

/** Props shared by every blog article page, fed to StandaloneArticle. */
export function blogArticleProps(slug) {
  const article = getBlogArticle(slug);
  return {
    metaTitle: article.metaTitle,
    metaDescription: article.metaDescription,
    path: blogArticlePath(article),
    categoryLabel: article.category,
    headline: article.headline,
    headlineAccent: article.headlineAccent,
    dateline: formatBlogDate(article.datePublished),
    keywords: article.keywords,
    // The FAQ is written into the article body (visible text, better for AI answer
    // extraction); the FAQPage schema is still emitted from `faqs`.
    renderFaqSection: false,
    schemaType: 'BlogPosting',
    author: BLOG_AUTHOR,
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    readingMinutes: article.readingMinutes,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Blog', path: BLOG_PATH },
      { name: article.headline, path: blogArticlePath(article) },
    ],
    relatedPages: article.relatedPages,
    relatedArticles: relatedBlogArticles(slug),
  };
}
